import React from 'react';
import { motion } from 'framer-motion';
import { Truck, DollarSign, PhoneCall, Mountain, MapPin, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT } from '../utils/i18n';
export function WhyUs() {
  const t = useT();
  const features = [{
    title: t('why.feat1.title'),
    description: t('why.feat1.desc'),
    icon: Truck
  }, {
    title: t('why.feat2.title'),
    description: t('why.feat2.desc'),
    icon: DollarSign
  }, {
    title: t('why.feat3.title'),
    description: t('why.feat3.desc'),
    icon: PhoneCall
  }, {
    title: t('why.feat4.title'),
    description: t('why.feat4.desc'),
    icon: Mountain
  }];
  return <section className="py-16 md:py-20 dark:bg-[#474443] transition-colors duration-300 border-y border-stone-200 dark:border-[#3f3d3c] bg-[#F2EDE0] relative overflow-hidden" id="why-us" data-id="element-1606">
      {/* Subtle decorative accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-orange/40 to-transparent" data-id="element-1607" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1608">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start" data-id="element-1609">
          {/* LEFT COLUMN — Brand statement */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="lg:col-span-5 lg:sticky lg:top-24" data-id="element-1610">
            <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-brand-orange/10 border border-brand-orange/25" data-id="element-1611">
              <MapPin className="w-3.5 h-3.5 text-brand-orange" data-id="element-1612" />
              <span className="text-[11px] font-bold text-brand-orange uppercase tracking-[0.14em]" data-id="element-1613">
                {t('why.localOwned')}
              </span>
            </div>

            <p className="text-brand-charcoal dark:text-white text-lg md:text-xl leading-relaxed font-medium font-[Inter,_sans-serif] mb-8" data-id="element-1614">
              {t('why.localBody')}
            </p>

            <Link to="/local-vs-national" className="group inline-flex items-center gap-2 text-brand-orange font-bold text-sm border-b-2 border-brand-orange/40 hover:border-brand-orange pb-1 transition-colors" data-id="element-1615">
              See how we stack up vs. national marketplaces
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-1616" />
            </Link>
          </motion.div>

          {/* RIGHT COLUMN — Feature grid */}
          <div className="lg:col-span-7" data-id="element-1617">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1618">
              {features.map((feature, index) => <motion.div key={feature.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.08,
              duration: 0.45,
              ease: 'easeOut'
            }} whileHover={{
              y: -3
            }} className="group relative p-5 rounded-xl bg-white/60 dark:bg-white/[0.04] border border-stone-200/80 dark:border-white/10 hover:border-brand-orange/40 dark:hover:border-brand-orange/40 hover:bg-white dark:hover:bg-white/[0.07] transition-colors" data-id="element-1619">
                  {/* Index number — subtle editorial detail */}
                  <span className="absolute top-4 right-4 text-[10px] font-bold text-brand-charcoal/30 dark:text-white/25 tracking-widest font-mono" data-id="element-1620">
                    0{index + 1}
                  </span>

                  <div className="w-11 h-11 bg-brand-dim/10 dark:bg-brand-dim/15 rounded-lg flex items-center justify-center mb-4 border border-brand-dim/20 dark:border-brand-dim/25 group-hover:bg-brand-dim/15 dark:group-hover:bg-brand-dim/20 transition-colors" data-id="element-1621">
                    <feature.icon className="w-5 h-5 text-brand-dim" data-id="element-1622" />
                  </div>

                  <h3 className="font-heading text-base font-bold text-brand-charcoal dark:text-white mb-1.5" data-id="element-1623">
                    {feature.title}
                  </h3>
                  <p className="text-brand-charcoal/65 dark:text-white/60 text-sm leading-relaxed" data-id="element-1624">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}